import { useCallback, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { Code, ConnectError } from '@connectrpc/connect'
import { useMutation, useTransport } from '@connectrpc/connect-query'
import type { ProcurementPlanAutoResubmitSetting } from '@ebalance/proto/bffuser/v1/plan_auto_resubmit_setting_model_pb'
import { updateProcurementPlanAutoResubmitSetting } from '@ebalance/proto/bffuser/v1/plan_auto_resubmit_setting_service-PlanAutoResubmitSettingService_connectquery'
import {
  type ListProcurementPlanAutoResubmitSettingsResponse,
  ListProcurementPlanAutoResubmitSettingsResponseSchema,
  UpdateProcurementPlanAutoResubmitSettingRequestSchema,
} from '@ebalance/proto/bffuser/v1/plan_auto_resubmit_setting_service_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useErrorToast } from '~/hooks/useErrorToast'
import { autoResubmitSettingsQueryKey } from '~/pages/ProcurementPlanList/hooks/useListProcurementPlanAutoResubmitSettings'
import {
  autoResubmitUnitKey,
  isNotOlderAutoResubmitSetting,
  isSameAutoResubmitUnit,
} from '~/pages/ProcurementPlanList/models/autoResubmitSetting'

type UseAutoResubmitSettingSaveResult = {
  /** 保存中 (先行変更の再取得中を含む) の単位の key (`autoResubmitUnitKey`) */
  savingKeys: ReadonlySet<string>
  save: (unit: ProcurementPlanAutoResubmitSetting, enabled: boolean) => void
}

/**
 * 自動再提出の設定を 1 単位ずつ保存する (AC-PAR-002 / 011)。
 *
 * 画面は保存済みの値だけを描く (先に切り替えて失敗時に戻すことはしない)。 成功時は応答の
 * 単位で cache を置き換え、 その updatedAt が同じ単位の次の楽観ロック基準になる。
 * 先行変更 (Aborted) は最新値を読み直してから知らせ、 読み直しが終わるまでその単位を
 * 保存中のままにする (古い基準で送り直して再び競合させないため)。
 */
export function useAutoResubmitSettingSave(): UseAutoResubmitSettingSaveResult {
  const { t } = useTranslation()
  const showErrorToast = useErrorToast()
  const queryClient = useQueryClient()
  const transport = useTransport()
  const { mutateAsync } = useMutation(updateProcurementPlanAutoResubmitSetting)
  const [savingKeys, setSavingKeys] = useState<ReadonlySet<string>>(new Set())

  const save = useCallback(
    (unit: ProcurementPlanAutoResubmitSetting, enabled: boolean) => {
      const key = autoResubmitUnitKey(unit)
      const queryKey = autoResubmitSettingsQueryKey(transport)
      setSavingKeys((prev) => new Set(prev).add(key))
      const run = async (): Promise<void> => {
        try {
          const response = await mutateAsync(
            create(UpdateProcurementPlanAutoResubmitSettingRequestSchema, {
              areaCode: unit.areaCode,
              category: unit.category,
              enabled,
              // backend は µs で比べるので、 Date を経由せず取得した Timestamp をそのまま送る
              expectedUpdatedAt: unit.updatedAt,
            }),
          )
          const saved = response.setting
          if (saved === undefined) {
            throw new Error('update response has no setting')
          }
          queryClient.setQueryData<
            ListProcurementPlanAutoResubmitSettingsResponse | undefined
          >(queryKey, (old) =>
            old === undefined
              ? old
              : create(ListProcurementPlanAutoResubmitSettingsResponseSchema, {
                  featureEnabled: old.featureEnabled,
                  settings: old.settings.map((s) =>
                    isSameAutoResubmitUnit(s, saved) &&
                    isNotOlderAutoResubmitSetting(saved, s)
                      ? saved
                      : s,
                  ),
                }),
          )
          // 先行変更の読み直しなどの取得がこの保存の commit より前に走っていると、
          // その古い応答が後から届いて保存した値を上書きする。 取得中なら取り消して
          // 読み直す (この保存を含む一覧を取り直す)。 data が無い (先行変更の読み直しで
          // 取得中に戻した) 間は invalidate が進行中の取得に相乗りするので、 先に取り消す
          if (queryClient.isFetching({ queryKey }) > 0) {
            await queryClient.cancelQueries({ queryKey })
            void queryClient.invalidateQueries({ queryKey })
          }
        } catch (err) {
          if (err instanceof ConnectError && err.code === Code.Aborted) {
            // 古い値を残したまま読み直さず、 取得中 (未確定) に戻して取り直す。
            // 読み直しが失敗した時は取得失敗の表示と再読み込みになり、 古い値と基準で
            // 操作させない (AC-PAR-010)。 知らせは読み直せた時だけ出す (文言が
            // 「最新の設定を読み込んだので」のため)
            await queryClient.resetQueries({ queryKey })
            // 読み直しが別の保存の取り直しで取り消された時は、 その取り直しを待つ
            // (進行中が無ければ取り直す)。 モーダルを閉じて query が消えれば抜ける
            for (
              let attempt = 0;
              attempt < 3 &&
              queryClient.getQueryState(queryKey)?.status === 'pending';
              attempt++
            ) {
              await queryClient.refetchQueries(
                { queryKey },
                { cancelRefetch: false },
              )
            }
            if (queryClient.getQueryState(queryKey)?.status === 'success') {
              showErrorToast({
                title: t(
                  'page:procurementPlanList.autoResubmitSettings.conflict',
                ),
              })
            }
            return
          }
          // backend の message は内部識別子を含みうるので spec の文言に寄せる
          showErrorToast({
            title: t(
              'page:procurementPlanList.autoResubmitSettings.saveFailed',
            ),
          })
        } finally {
          setSavingKeys((prev) => {
            const next = new Set(prev)
            next.delete(key)
            return next
          })
        }
      }
      void run()
    },
    [mutateAsync, queryClient, showErrorToast, t, transport],
  )

  return { savingKeys, save }
}
